import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { useActiveHostEvent } from '@/hooks/use-active-host-event';
import { listEvents } from '@/lib/events-api';
import { listEventActivity } from '@/lib/submissions-api';
import { apiFetch } from '@/lib/api';
import { Image, Users, EyeOff, BookOpen, Tv, ChevronRight, AlertCircle, UserPlus, Download, Crown, Briefcase, Copy, UserX, Send, X, BarChart2 } from 'lucide-react';
import TierBadge from '@/components/TierBadge';
import CapMeter from '@/components/CapMeter';
import { cloneEvent, createTransfer, cancelTransfer, getEventTransfer } from '@/lib/pro-toolkit-api';
import { eventStatusLabel, statusPillClasses } from '@/lib/event-labels';

function relativeTime(iso) {
  try {
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ''; }
}

export default function HostDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ready } = useRequireAuth();
  const { setActiveEventId } = useActiveHostEvent();
  const [events, setEvents] = useState([]);
  const [coHosted, setCoHosted] = useState([]);
  const [attending, setAttending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [activity, setActivity] = useState([]);
  const [quotaData, setQuotaData] = useState(null);
  // Phase 14 — Clone modal state
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloneStart, setCloneStart] = useState('');
  const [cloneEnd, setCloneEnd] = useState('');
  const [cloneBusy, setCloneBusy] = useState(false);
  const [cloneError, setCloneError] = useState(null);
  // Phase 14 — Transfer modal state
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferPassword, setTransferPassword] = useState('');
  const [transferKeepCohost, setTransferKeepCohost] = useState(true);
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState(null);
  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const isPro = (user?.account_type || '').toUpperCase() === 'PROFESSIONAL';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await listEvents();
      const allHosted = body.hosted || [];
      const allCoHosted = body.co_hosted || [];
      setEvents(allHosted);
      setCoHosted(allCoHosted);
      setAttending(body.attending || []);
      if ((allHosted.length > 0 || allCoHosted.length > 0) && !selectedId) {
        const priority = { COLLECTING: 0, SCHEDULED: 1, CURATING: 2, LOOKBOOK_LIVE: 3, PAUSED: 4, DRAFT: 5, ARCHIVED: 6, CANCELLED: 7 };
        const pool = allHosted.length > 0 ? allHosted : allCoHosted;
        const sorted = [...pool].sort((a, b) => {
          const pa = a.pending_approvals_count || 0;
          const pb = b.pending_approvals_count || 0;
          if (pa !== pb) return pb - pa;
          return (priority[a.status] ?? 9) - (priority[b.status] ?? 9);
        });
        setSelectedId(sorted[0].id);
      }
    } catch { /* retry on focus */ }
    finally { setLoading(false); }
  }, [selectedId]);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  const selected = events.find(e => e.id === selectedId) || coHosted.find(e => e.id === selectedId) || events[0] || coHosted[0] || null;
  const viewerRole = coHosted.some(e => e.id === selected?.id) ? 'CO_HOST' : 'OWNER';
  const isOwner = viewerRole === 'OWNER';

  // Persist active event + viewer role for navbar and sub-pages
  useEffect(() => {
    if (selected?.id) setActiveEventId(selected.id);
    if (selected?.id) localStorage.setItem('activeEventViewerRole', viewerRole);
  }, [selected?.id, setActiveEventId, viewerRole]);

  useEffect(() => {
    if (!selected?.id) { setActivity([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await listEventActivity(selected.id, 12);
        if (!cancelled) setActivity(res.activity || []);
      } catch { if (!cancelled) setActivity([]); }
    })();
    return () => { cancelled = true; };
  }, [selected?.id]);

  // Fetch upload-quota for selected event (powers cap meters)
  useEffect(() => {
    if (!selected?.id) { setQuotaData(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const q = await apiFetch(`/api/v1/events/${encodeURIComponent(selected.id)}/upload-quota`);
        if (!cancelled) setQuotaData(q);
      } catch { if (!cancelled) setQuotaData(null); }
    })();
    return () => { cancelled = true; };
  }, [selected?.id]);

  // Phase 14 — Poll pending transfer for selected event
  useEffect(() => {
    if (!selected?.id) { setPendingTransfer(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await getEventTransfer(selected.id);
        if (!cancelled) setPendingTransfer(res.transfer || null);
      } catch { if (!cancelled) setPendingTransfer(null); }
    })();
    return () => { cancelled = true; };
  }, [selected?.id]);

  // Phase 14 — Clone handler
  async function onClone() {
    if (!selected?.id) return;
    setCloneError(null);
    if (!cloneName.trim()) { setCloneError('Give the new event a name.'); return; }
    const isoOrThrow = (v, label) => {
      const s = v.trim();
      if (!s) throw new Error(`${label} is required`);
      const iso = s.includes('T') && !/[+Z]/.test(s.slice(-6)) ? `${s}:00+00:00` : s;
      if (isNaN(new Date(iso).getTime())) throw new Error(`${label} isn't a valid date`);
      return iso;
    };
    setCloneBusy(true);
    try {
      const start = isoOrThrow(cloneStart, 'Start date');
      const end = isoOrThrow(cloneEnd, 'End date');
      const res = await cloneEvent(selected.id, { name: cloneName.trim(), start_at: start, end_at: end });
      setCloneOpen(false);
      setCloneName(''); setCloneStart(''); setCloneEnd('');
      await load();
      setSelectedId(res.event.id);
    } catch (e) {
      setCloneError(e?.message || 'Could not clone event.');
    } finally {
      setCloneBusy(false);
    }
  }

  // Phase 14 — Transfer handler
  async function onCreateTransfer() {
    if (!selected?.id) return;
    setTransferError(null);
    if (!transferEmail.trim() || !transferPassword) {
      setTransferError("Enter the recipient's email and your password.");
      return;
    }
    setTransferBusy(true);
    try {
      const res = await createTransfer(selected.id, {
        email: transferEmail.trim(),
        keep_as_cohost: transferKeepCohost,
        password: transferPassword,
      });
      setPendingTransfer(res.transfer);
      setTransferOpen(false);
      setTransferEmail(''); setTransferPassword(''); setTransferKeepCohost(true);
    } catch (e) {
      setTransferError(e?.message || 'Could not send transfer.');
    } finally {
      setTransferBusy(false);
    }
  }

  // Phase 14 — Cancel transfer handler
  async function onCancelTransfer() {
    if (!pendingTransfer) return;
    try {
      const res = await cancelTransfer(pendingTransfer.id);
      setPendingTransfer(res.transfer?.status === 'PENDING' ? res.transfer : null);
      setCancelConfirmOpen(false);
    } catch { /* ignore */ }
  }

  const stats = selected?.submission_stats;
  const statItems = stats ? [
    { label: 'Uploads', value: stats.total, icon: Image, highlight: false },
    { label: 'Contributors', value: stats.contributors ?? selected?.contributor_count ?? 0, icon: Users },
    { label: 'Unreviewed', value: stats.unreviewed, icon: EyeOff, highlight: stats.unreviewed > 0 },
    { label: 'Lookbook', value: stats.lookbook_selected ?? 0, icon: BookOpen },
    { label: 'Frame', value: stats.frame_selected ?? 0, icon: Tv },
  ] : [];

  const tiles = [
    { label: 'Review New Views', icon: Image, onClick: () => navigate(`/host-review?event_id=${selected?.id}&status=new`), testId: 'host-tile-review' },
    { label: 'Build Lookbook', icon: BookOpen, badge: selected?.lookbook?.state === 'LIVE' ? 'LIVE' : selected?.lookbook?.state === 'PAUSED' ? 'PAUSED' : null,
      onClick: () => navigate(`/${selected?.lookbook?.state === 'LIVE' || selected?.lookbook?.state === 'PAUSED' ? 'host-lookbook-live' : 'host-lookbook'}?event_id=${selected?.id}`), testId: 'host-tile-lookbook' },
    { label: 'Export Frames', icon: Download, onClick: () => navigate(`/frames?event_id=${selected?.id}`), testId: 'host-tile-frames' },
    { label: 'Invite Guests', icon: UserPlus, onClick: () => navigate(`/host-guests?event_id=${selected?.id}`), testId: 'host-tile-invite' },
  ];

  return (
  <>
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="host-dashboard-screen">
      <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-1" data-testid="host-dashboard-title">Host Dashboard</h1>
      <p className="text-sm text-[#5B6472] mb-6" data-testid="host-dashboard-welcome">Welcome back, {user?.first_name}. Here&apos;s what&apos;s happening.</p>

      {loading ? (
        <div className="flex justify-center py-8" data-testid="host-dashboard-loading">
          <div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : events.length === 0 && coHosted.length === 0 ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-6" data-testid="host-no-events">
          <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest mb-2 uppercase">No events yet</p>
          <p className="text-sm text-[#5B6472] mb-4">Create your first event to generate an Event ID, QR code, and invite links.</p>
          <button onClick={() => navigate('/create-event')}
            className="h-10 px-6 bg-[#146BFF] text-white font-semibold rounded-lg hover:bg-[#0F55CC] transition-colors"
            data-testid="host-create-first-btn">Create Event</button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {events.length > 1 && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="host-event-switcher">
              <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest mb-2 uppercase">Switch event</p>
              <div className="flex flex-col gap-1">
                {events.map(e => (
                  <button key={e.id} onClick={() => setSelectedId(e.id)}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${e.id === selectedId ? 'bg-[#EFF5FF] text-[#146BFF] font-bold' : 'bg-[#F3F5F8] text-[#0B1B46] hover:bg-[#E3E8EF]'}`}
                    data-testid={`host-switch-${e.id}`}>
                    <span className="truncate">{e.name}</span>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${statusPillClasses(e.status)}`}>{eventStatusLabel(e.status)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Co-hosted events */}
          {coHosted.length > 0 && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="host-cohosted-card">
              <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest mb-2 uppercase">Events you help manage</p>
              <div className="flex flex-col gap-1">
                {coHosted.map(e => (
                  <button key={e.id} onClick={() => setSelectedId(e.id)}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${e.id === selectedId ? 'bg-[#EFF5FF] text-[#146BFF] font-bold' : 'bg-[#F3F5F8] text-[#0B1B46] hover:bg-[#E3E8EF]'}`}
                    data-testid={`host-cohosted-switch-${e.id}`}>
                    <span className="truncate">{e.name}</span>
                    <span className={`ml-2 flex-shrink-0 px-2 py-0.5 text-[10px] font-bold rounded-full ${statusPillClasses(e.status)}`}>{eventStatusLabel(e.status)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {selected && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="host-active-event-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-extrabold text-[#146BFF] tracking-widest">ACTIVE EVENT</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${statusPillClasses(selected.status)}`} data-testid="host-active-status">{eventStatusLabel(selected.status)}</span>
              </div>
              <h2 className="text-lg font-extrabold text-[#0B1B46] mb-2" data-testid="host-active-name">{selected.name}</h2>
              <div className="flex items-center gap-2 mb-2">
                <TierBadge tier={selected.is_legacy_uncapped ? 'LEGACY' : (selected.premium_tier || 'FREE')} size="sm" testId="host-active-tier-badge" />
                {isOwner && !selected.is_legacy_uncapped && (selected.premium_tier || 'FREE') === 'FREE' && (
                  <button
                    onClick={() => navigate(`/host-premium?event_id=${encodeURIComponent(selected.id)}`)}
                    className="text-[11px] font-bold text-[#146BFF] hover:underline flex items-center gap-0.5"
                    data-testid="host-active-upgrade-link"
                  >
                    <Crown className="w-3 h-3" /> Upgrade
                  </button>
                )}
              </div>
              {/* Cap meters for FREE non-legacy events (owner only) */}
              {isOwner && quotaData && !quotaData.is_legacy_uncapped && quotaData.tier === 'FREE' && (
                <div className="mb-3 p-3 bg-[#F8FAFC] rounded-lg" data-testid="host-cap-meters">
                  <CapMeter label="Uploads" current={quotaData.uploads?.current ?? 0} limit={quotaData.uploads?.unlimited ? null : quotaData.uploads?.limit} warn={!quotaData.uploads?.unlimited && quotaData.uploads?.current >= (quotaData.uploads?.limit || Infinity) * 0.8} blocked={!quotaData.uploads?.unlimited && quotaData.uploads?.current >= (quotaData.uploads?.limit || Infinity)} testId="host-cap-uploads" />
                  <CapMeter label="Contributors" current={selected.contributor_count ?? 0} limit={quotaData.is_legacy_uncapped ? null : (quotaData.contributor_limit ?? null)} warn={!quotaData.is_legacy_uncapped && quotaData.contributor_limit && (selected.contributor_count ?? 0) >= quotaData.contributor_limit * 0.8} blocked={!quotaData.is_legacy_uncapped && quotaData.contributor_limit && (selected.contributor_count ?? 0) >= quotaData.contributor_limit} testId="host-cap-contributors" />
                </div>
              )}
              <div className="flex flex-col gap-1 text-sm text-[#5B6472]">
                <span>{selected.start_at ? new Date(selected.start_at).toLocaleDateString() : ''}</span>
                <span data-testid="host-contributor-count">{selected.contributor_count ?? 0} contributors</span>
                <span className="font-mono" data-testid="host-event-code">{selected.current_event_id}</span>
              </div>
              {(selected.pending_approvals_count || 0) > 0 && (
                <button onClick={() => navigate(`/host-guests?event_id=${selected.id}`)}
                  className="w-full flex items-center gap-2 mt-3 px-3 py-2 bg-[#FFF6E5] border border-[#B86500] rounded-lg text-sm text-[#B86500] font-bold hover:bg-[#FBEEDA] transition-colors"
                  data-testid="host-pending-approvals-cta">
                  <AlertCircle className="w-4 h-4" />
                  <span className="flex-1 text-left" data-testid="host-pending-approvals-count">{selected.pending_approvals_count} pending approval{selected.pending_approvals_count === 1 ? '' : 's'} — review now</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
              {isOwner && (
                <button onClick={() => navigate(`/event-created?event_id=${encodeURIComponent(selected.id)}`)}
                  className="w-full h-10 mt-3 bg-[#146BFF] hover:bg-[#0F55CC] text-white font-semibold rounded-lg transition-colors"
                  data-testid="host-view-details-btn">View Event Details</button>
              )}
            </div>
          )}

          {statItems.length > 0 && (
            <div className="flex gap-3 overflow-x-auto pb-1" data-testid="host-dashboard-stats">
              {statItems.map(({ label, value, icon: Icon, highlight }) => (
                <div key={label} className={`min-w-[110px] border rounded-xl p-3 flex flex-col gap-0.5 ${highlight ? 'border-[#B86500] bg-[#FFF6E5]' : 'border-[#E3E8EF] bg-white'}`}
                  data-testid={`host-dashboard-stat-${label.toLowerCase()}`}>
                  <Icon className="w-4 h-4 text-[#146BFF]" />
                  <span className="text-xl font-extrabold text-[#0B1B46]" data-testid={`host-dashboard-stat-${label.toLowerCase()}-value`}>{value}</span>
                  <span className="text-[11px] text-[#5B6472] font-semibold">{label}</span>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3" data-testid="host-primary-actions">
            {tiles.map(({ label, icon: Icon, onClick, badge, testId }) => (
              <button key={label} onClick={onClick}
                className="relative bg-white border border-[#E3E8EF] rounded-xl p-4 flex flex-col items-start gap-2 hover:border-[#146BFF] hover:shadow-sm transition-all text-left focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
                data-testid={testId}>
                <div className="w-10 h-10 rounded-full bg-[#EFF5FF] flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#146BFF]" />
                </div>
                <span className="text-sm font-bold text-[#0B1B46]">{label}</span>
                {badge && <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[9px] font-extrabold bg-[#F3F5F8] text-[#5B6472] rounded">{badge}</span>}
              </button>
            ))}
          </div>

          {/* Phase 14 — Advanced actions card (owner only) */}
          {isOwner && selected && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="host-advanced-card">
              <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest mb-3 uppercase">Advanced</p>

              {/* Client dashboard link (PRO only) */}
              {isPro && (
                <button
                  onClick={() => navigate('/pro-clients')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#F3F5F8] hover:bg-[#E3E8EF] transition-colors mb-2 text-left"
                  data-testid="host-adv-client-dashboard"
                >
                  <Briefcase className="w-4 h-4 text-[#0B1B46] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-[#0B1B46]">Client dashboard</p>
                    <p className="text-xs text-[#5B6472] mt-0.5">All events you host, in one Pro view.</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#5B6472] flex-shrink-0" />
                </button>
              )}

              {/* Analytics link (PRO only) */}
              {isPro && (
                <button
                  onClick={() => navigate('/pro-analytics')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#F3F5F8] hover:bg-[#E3E8EF] transition-colors mb-2 text-left"
                  data-testid="host-adv-analytics"
                >
                  <BarChart2 className="w-4 h-4 text-[#0B1B46] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-[#0B1B46]">Cross-event analytics</p>
                    <p className="text-xs text-[#5B6472] mt-0.5">Aggregate stats across all your events.</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#5B6472] flex-shrink-0" />
                </button>
              )}

              {/* Team seats link (PRO only) */}
              {isPro && (
                <button
                  onClick={() => navigate('/pro-team')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#F3F5F8] hover:bg-[#E3E8EF] transition-colors mb-2 text-left"
                  data-testid="host-adv-team"
                >
                  <Users className="w-4 h-4 text-[#0B1B46] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-[#0B1B46]">Team seats</p>
                    <p className="text-xs text-[#5B6472] mt-0.5">Invite teammates and manage co-host access.</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#5B6472] flex-shrink-0" />
                </button>
              )}

              {/* Duplicate event */}
              <button
                onClick={() => {
                  const copyName = selected ? `${selected.name} (copy)` : '';
                  setCloneName(copyName);
                  const inMonth = new Date(Date.now() + 30 * 86400000);
                  const isoDay = (d) => d.toISOString().slice(0, 10);
                  setCloneStart(`${isoDay(inMonth)}T18:00`);
                  setCloneEnd(`${isoDay(inMonth)}T23:00`);
                  setCloneError(null);
                  setCloneOpen(true);
                }}
                disabled={!selected}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#F3F5F8] hover:bg-[#E3E8EF] transition-colors mb-2 text-left disabled:opacity-50"
                data-testid="host-adv-clone-btn"
              >
                <Copy className="w-4 h-4 text-[#0B1B46] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold text-[#0B1B46]">Duplicate this event</p>
                  <p className="text-xs text-[#5B6472] mt-0.5">Copy settings into a fresh DRAFT. No members or media.</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#5B6472] flex-shrink-0" />
              </button>

              {/* Transfer ownership OR pending transfer indicator */}
              {pendingTransfer ? (
                <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#EFF5FF] border border-[#DCE7FF]" data-testid="host-adv-pending-transfer">
                  <Send className="w-4 h-4 text-[#146BFF] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-[#0B1B46]">Transfer pending</p>
                    <p className="text-xs text-[#5B6472] mt-0.5">
                      Sent to {pendingTransfer.to_email} &middot; expires{' '}
                      {new Date(pendingTransfer.expires_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <button
                    onClick={() => setCancelConfirmOpen(true)}
                    className="text-xs font-bold text-[#DC2626] hover:underline flex-shrink-0"
                    data-testid="host-adv-cancel-transfer-btn"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setTransferError(null); setTransferOpen(true); }}
                  disabled={!selected}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#FEF2F2] border border-[#FEE2E2] hover:bg-[#FDE8E8] transition-colors text-left disabled:opacity-50"
                  data-testid="host-adv-transfer-btn"
                >
                  <UserX className="w-4 h-4 text-[#DC2626] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-[#DC2626]">Transfer ownership</p>
                    <p className="text-xs text-[#5B6472] mt-0.5">Hand this event off to a client. You&apos;ll re-auth.</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#DC2626] flex-shrink-0" />
                </button>
              )}
            </div>
          )}

          {activity.length > 0 && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="host-dashboard-activity">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Recent Activity</span>
                <button onClick={() => navigate(`/host-vault?event_id=${selected?.id}`)} className="text-[#146BFF] text-xs font-bold hover:underline" data-testid="host-activity-view-all">View all</button>
              </div>
              {activity.map(row => (
                <div key={row.id} className="flex items-center gap-3 py-2 border-b border-[#E3E8EF] last:border-0" data-testid={`host-activity-item-${row.id}`}>
                  <div className="w-8 h-8 rounded-full bg-[#EFF5FF] flex items-center justify-center text-[#146BFF] text-xs font-extrabold flex-shrink-0">
                    {(row.actor_name?.[0] || '?').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0B1B46] truncate" data-testid={`host-activity-item-${row.id}-message`}>{row.message}</p>
                    <p className="text-[11px] text-[#5B6472]">{relativeTime(row.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {attending.length > 0 && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="host-attending-card">
              <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest mb-2 uppercase">Events you&apos;re attending</p>
              {attending.map(({ event }) => (
                <button key={event.id} onClick={() => navigate('/contrib-event')}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-[#F3F5F8] hover:bg-[#E3E8EF] text-sm text-[#0B1B46] mb-1 transition-colors"
                  data-testid={`host-attending-${event.id}`}>
                  <span className="truncate">{event.name}</span>
                  <ChevronRight className="w-4 h-4 text-[#5B6472]" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>

    {/* Phase 14 — Clone modal */}
    {cloneOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1B46]/55 p-4" onClick={() => setCloneOpen(false)}>
        <div
          className="bg-white rounded-2xl p-5 w-full max-w-md flex flex-col gap-2"
          onClick={(e) => e.stopPropagation()}
          data-testid="host-clone-modal"
        >
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-extrabold text-[#0B1B46]">Duplicate event</h3>
            <button onClick={() => setCloneOpen(false)} className="p-1 hover:bg-[#F3F5F8] rounded" aria-label="Close">
              <X className="w-5 h-5 text-[#5B6472]" />
            </button>
          </div>
          <p className="text-sm text-[#5B6472]">Copies settings only &mdash; no members, media, invitations, lookbook, or payments.</p>

          <label className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide mt-2" htmlFor="clone-name">New event name</label>
          <input
            id="clone-name"
            type="text"
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            placeholder="e.g. Sarah & James Wedding (copy)"
            className="w-full border border-[#E3E8EF] rounded-lg px-3 py-2.5 text-sm text-[#0B1B46] placeholder:text-[#5B6472] focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
            data-testid="host-clone-name-input"
          />

          <label className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide mt-2" htmlFor="clone-start">Start date &amp; time</label>
          <input
            id="clone-start"
            type="datetime-local"
            value={cloneStart}
            onChange={(e) => setCloneStart(e.target.value)}
            className="w-full border border-[#E3E8EF] rounded-lg px-3 py-2.5 text-sm text-[#0B1B46] focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
            data-testid="host-clone-start-input"
          />

          <label className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide mt-2" htmlFor="clone-end">End date &amp; time</label>
          <input
            id="clone-end"
            type="datetime-local"
            value={cloneEnd}
            onChange={(e) => setCloneEnd(e.target.value)}
            className="w-full border border-[#E3E8EF] rounded-lg px-3 py-2.5 text-sm text-[#0B1B46] focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
            data-testid="host-clone-end-input"
          />

          {cloneError && <p className="text-xs font-semibold text-[#DC2626] mt-1" data-testid="host-clone-error">{cloneError}</p>}

          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setCloneOpen(false)}
              className="flex-1 h-10 border border-[#E3E8EF] bg-white hover:bg-[#F3F5F8] text-[#0B1B46] font-semibold rounded-lg transition-colors"
              data-testid="host-clone-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={onClone}
              disabled={cloneBusy}
              className="flex-1 h-10 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-60 text-white font-semibold rounded-lg transition-colors"
              data-testid="host-clone-submit-btn"
            >
              {cloneBusy ? 'Cloning\u2026' : 'Duplicate'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Phase 14 — Transfer modal */}
    {transferOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1B46]/55 p-4" onClick={() => setTransferOpen(false)}>
        <div
          className="bg-white rounded-2xl p-5 w-full max-w-md flex flex-col gap-2"
          onClick={(e) => e.stopPropagation()}
          data-testid="host-transfer-modal"
        >
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-extrabold text-[#0B1B46]">Transfer event ownership</h3>
            <button onClick={() => setTransferOpen(false)} className="p-1 hover:bg-[#F3F5F8] rounded" aria-label="Close">
              <X className="w-5 h-5 text-[#5B6472]" />
            </button>
          </div>
          <p className="text-sm text-[#5B6472]">
            Sends the recipient a 14-day accept link. Once they accept, they become the host of this event. Paid tiers and contributors stay put.
          </p>

          <label className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide mt-2" htmlFor="transfer-email">Recipient email</label>
          <input
            id="transfer-email"
            type="email"
            value={transferEmail}
            onChange={(e) => setTransferEmail(e.target.value)}
            placeholder="client@example.com"
            className="w-full border border-[#E3E8EF] rounded-lg px-3 py-2.5 text-sm text-[#0B1B46] placeholder:text-[#5B6472] focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
            data-testid="host-transfer-email-input"
          />

          <div className="flex items-center gap-3 mt-2">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide">Stay on as Co-Host</p>
              <p className="text-xs text-[#5B6472] mt-0.5">Recommended &mdash; you can still help with settings and moderation.</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={transferKeepCohost}
              onClick={() => setTransferKeepCohost(!transferKeepCohost)}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#146BFF] ${transferKeepCohost ? 'bg-[#146BFF]' : 'bg-[#D1D5DB]'}`}
              data-testid="host-transfer-keep-cohost"
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${transferKeepCohost ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          <label className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide mt-2" htmlFor="transfer-password">Confirm your password</label>
          <input
            id="transfer-password"
            type="password"
            value={transferPassword}
            onChange={(e) => setTransferPassword(e.target.value)}
            placeholder="Password"
            className="w-full border border-[#E3E8EF] rounded-lg px-3 py-2.5 text-sm text-[#0B1B46] placeholder:text-[#5B6472] focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
            data-testid="host-transfer-password-input"
          />

          {transferError && <p className="text-xs font-semibold text-[#DC2626] mt-1" data-testid="host-transfer-error">{transferError}</p>}

          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setTransferOpen(false)}
              className="flex-1 h-10 border border-[#E3E8EF] bg-white hover:bg-[#F3F5F8] text-[#0B1B46] font-semibold rounded-lg transition-colors"
              data-testid="host-transfer-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={onCreateTransfer}
              disabled={transferBusy}
              className="flex-1 h-10 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-60 text-white font-semibold rounded-lg transition-colors"
              data-testid="host-transfer-submit-btn"
            >
              {transferBusy ? 'Sending\u2026' : 'Send transfer'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Phase 14 — Cancel transfer confirm dialog */}
    {cancelConfirmOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1B46]/55 p-4" onClick={() => setCancelConfirmOpen(false)}>
        <div
          className="bg-white rounded-2xl p-5 w-full max-w-sm flex flex-col gap-3"
          onClick={(e) => e.stopPropagation()}
          data-testid="host-cancel-transfer-dialog"
        >
          <h3 className="text-lg font-extrabold text-[#0B1B46]">Cancel transfer?</h3>
          <p className="text-sm text-[#5B6472]">The recipient&apos;s accept link will stop working.</p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => setCancelConfirmOpen(false)}
              className="flex-1 h-10 border border-[#E3E8EF] bg-white hover:bg-[#F3F5F8] text-[#0B1B46] font-semibold rounded-lg transition-colors"
              data-testid="host-cancel-transfer-keep-btn"
            >
              Keep it
            </button>
            <button
              onClick={onCancelTransfer}
              className="flex-1 h-10 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-semibold rounded-lg transition-colors"
              data-testid="host-cancel-transfer-confirm-btn"
            >
              Cancel transfer
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}
